
import { supabase } from "@/integrations/supabase/client";
import { Feature, UserFeature } from "@/types";
import { networkService } from "@/services/network.service";
import logger from "@/utils/logger";
import { getCachedUserId, setCachedUser } from "@/services/auth/sessionCache";

/**
 * Get all available features from the database
 */
export const getAllFeatures = async (): Promise<Feature[]> => {
  try {
    const { data, error } = await supabase
      .from('features')
      .select('*')
      .order('name', { ascending: true });
      
    if (error) {
      console.error("Error fetching features:", error);
      throw error;
    }
    
    // Map each item in the data array to a Feature object
    return data.map((feature) => ({
      id: feature.id,
      name: feature.name,
      description: feature.description,
      isPremium: feature.is_premium,
      isEnabled: feature.is_enabled
    }));
  } catch (error) {
    console.error("Error in getAllFeatures:", error);
    return [];
  }
};

/**
 * Get all features enabled for the current user
 */
export const getUserFeatures = async (): Promise<UserFeature[]> => {
  if (!networkService.isOnline()) {
    // ✅ FIX: Ritorna features base predefinite in modalità offline invece di array vuoto
    logger.log("[features.service] Offline: ritorno features base predefinite");
    return getOfflineBaseFeatures();
  }

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      logger.log("No user session found, returning offline base features");
      return getOfflineBaseFeatures();
    }

    const { data, error } = await supabase
      .from('user_features')
      .select(`
        feature_key,
        features!inner (
          id,
          name,
          description,
          is_premium,
          is_enabled
        )
      `)
      .eq('user_id', userId);

    if (error) {
      console.error("Error fetching user features:", error);
      return getOfflineBaseFeatures();
    }

    return data.map((item): UserFeature => ({
      key: item.feature_key,
      feature: {
        id: (item.features as any).id,
        name: (item.features as any).name,
        description: (item.features as any).description,
        isPremium: (item.features as any).is_premium,
        isEnabled: (item.features as any).is_enabled
      }
    }));    
  } catch (error) {
    console.error("Error in getUserFeatures:", error);
    return getOfflineBaseFeatures();
  }
};

/**
 * ✅ FIX: Fornisce features base predefinite per modalità offline
 */
function getOfflineBaseFeatures(): UserFeature[] {
  return [
    {
      key: 'basic_lists',
      feature: {
        id: 'offline-basic-lists',
        name: 'basic_lists',
        description: 'Gestione liste base',
        isPremium: false,
        isEnabled: true
      }
    },
    {
      key: 'basic_products',
      feature: {
        id: 'offline-basic-products',
        name: 'basic_products',
        description: 'Gestione prodotti base',
        isPremium: false,
        isEnabled: true
      }
    },
    {
      key: 'basic_categories',
      feature: {
        id: 'offline-basic-categories',
        name: 'basic_categories',
        description: 'Gestione categorie base',
        isPremium: false,
        isEnabled: true
      }
    }
  ];
}

/**
 * Assign base features to a user
 */
export const assignBaseFeatures = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('assign_base_features', {
      p_user_id: userId
    });
    
    if (error) {
      console.error("Error assigning base features:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in assignBaseFeatures:", error);
    return false;
  }
};

/**
 * Assign premium features to a user
 */
export const assignPremiumFeatures = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('assign_premium_features', {
      p_user_id: userId
    });
    
    if (error) {
      console.error("Error assigning premium features:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in assignPremiumFeatures:", error);
    return false;
  }
};

/**
 * Revoke premium features from a user
 */
export const revokePremiumFeatures = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('revoke_premium_features', {
      p_user_id: userId
    });
    
    if (error) {
      console.error("Error revoking premium features:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in revokePremiumFeatures:", error);
    return false;
  }
};

/**
 * Check if a specific feature is enabled for the current user
 */
export const isFeatureEnabled = async (featureKey: string): Promise<boolean> => {
  if (!networkService.isOnline()) {
    // ✅ FIX: In modalità offline, abilita sempre le features base
    const baseFeatures = ['basic_lists', 'basic_products', 'basic_categories'];
    return baseFeatures.includes(featureKey);
  }

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      // ✅ FIX: Se non autenticato, abilita features base
      const baseFeatures = ['basic_lists', 'basic_products', 'basic_categories'];
      return baseFeatures.includes(featureKey);
    }
    
    // Check if the user has this feature in user_features
    const { data, error } = await supabase
      .from('user_features')
      .select('feature_key, features!inner(is_enabled)')
      .eq('user_id', userId)
      .eq('feature_key', featureKey)
      .single();
      
    if (error) {
      console.error(`Feature ${featureKey} not found or not enabled for user:`, error);
      // ✅ FIX: Fallback per features base
      const baseFeatures = ['basic_lists', 'basic_products', 'basic_categories'];
      return baseFeatures.includes(featureKey);
    }
    
    // Feature is enabled if it exists in user_features and is_enabled in features table
    return (data.features as any).is_enabled;
  } catch (error) {
    console.error(`Error checking if feature ${featureKey} is enabled:`, error);
    // ✅ FIX: Fallback per features base
    const baseFeatures = ['basic_lists', 'basic_products', 'basic_categories'];
    return baseFeatures.includes(featureKey);
  }
};

/**
 * Sync user features with the master features list.
 * Missing features are assigned based on the user's premium status.
 *
 * L'assegnazione passa SOLO dalle RPC `SECURITY DEFINER` (`assign_base_features` /
 * `assign_premium_features`), che girano come owner e bypassano RLS. La tabella
 * `user_features` non espone una policy INSERT per il ruolo `authenticated`,
 * quindi un upsert diretto dal client viola sempre RLS (Sentry CERALIA-94).
 * Le RPC sono idempotenti (`ON CONFLICT DO NOTHING`): assolvono lo scopo di
 * inserire le sole feature mancanti senza bisogno di leggere/diffare prima.
 *
 * @param userId - The identifier of the user to sync.
 * @param isPremium - Indicates if the user has premium access.
 * @returns Resolves when the synchronization is completed.
 */
export const syncUserFeatures = async (userId: string, isPremium: boolean): Promise<void> => {
  if (!networkService.isOnline()) return;

  await assignBaseFeatures(userId);

  if (isPremium) {
    await assignPremiumFeatures(userId);
  }
};
